import './ServiceOrderShow.scss';

import { CheckCircleFilled, WarningFilled } from '@ant-design/icons';
import { DateField, Show, TextField } from '@refinedev/antd';
import { useGo, useShow } from '@refinedev/core';
import { Tabs, Tag } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import ServiceOrderStatusTag from '@/components/serviceOrder/ServiceOrderStatusTag';
import { useUserContext } from '@/contexts/UserContext';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { SERVICE_ORDER_STATUS, ServiceOrder } from '@/model/ServiceOrder/ServiceOrder';
import { ServiceOrderItem } from '@/model/ServiceOrder/ServiceOrderItem';
import { useRepositoriesContext } from '@/repositories/RepositoriesContext';
import { RESOURCES } from '@/Resources';

import ServiceOrderShowHeaderButtons from './ServiceOrderShow/ServiceOrderShowHeaderButtons';
import ServiceOrderShowLogistic from './ServiceOrderShow/ServiceOrderShowLogistic';
import ServiceOrderShowServiceInfo from './ServiceOrderShow/ServiceOrderShowServiceInfo';

const ServiceOrderShow = () => {
  const { userWorkArea } = useUserContext();
  const go = useGo();
  const { serviceOrderRepository } = useRepositoriesContext();
  const { t } = useTranslation();
  const {
    result: serviceOrder,
    query: { isLoading, refetch },
  } = useShow<ServiceOrder>();
  const [serviceOrderItems, setServiceOrderItems] = useState<ServiceOrderItem[]>([]);
  const refetchAll = async () => {
    await refetch();
    if (serviceOrder?.id) {
      const items = await serviceOrderRepository.getServiceOrderItems(serviceOrder.id);

      setServiceOrderItems(items);
    }
  };

  const [modifingAdminData, setModifingAdminData] = useState<boolean>(false);

  /**
   * Redirect a edit para el caso pendiente de coordinación
   */

  useEffect(() => {
    if (
      serviceOrder?.id &&
      userWorkArea == USER_WORK_AREA.CUSTOMER_SERVICE &&
      (serviceOrder.status == SERVICE_ORDER_STATUS.PENDIENTE_DE_COORDINACION ||
        serviceOrder.status == SERVICE_ORDER_STATUS.COORDINADA)
    )
      go({
        to: { resource: RESOURCES.SERVICE_ORDERS, action: 'edit', id: serviceOrder.id },
        type: 'replace',
      });
  }, [go, serviceOrder, serviceOrderRepository, userWorkArea]);

  const hasTechnicianItem = useMemo(
    () =>
      serviceOrderItems.some(
        item => item.stock_technician_amount || item.package_item == undefined,
      ),
    [serviceOrderItems],
  );

  return (
    <Show
      isLoading={isLoading}
      headerButtons={({ editButtonProps, refreshButtonProps, deleteButtonProps }) =>
        serviceOrder && (
          <ServiceOrderShowHeaderButtons
            serviceOrder={serviceOrder}
            refineButtonsProps={{
              editButtonProps,
              refreshButtonProps: { ...refreshButtonProps, onClick: () => void refetchAll() },
              deleteButtonProps,
              listButtonProps: undefined,
            }}
            refetch={() => void refetchAll()}
            modifingAdminData={modifingAdminData}
          />
        )
      }>
      {serviceOrder && (
        <div className="ServiceOrderShow">
          <div className="ServiceOrderShowDataRow">
            <div className="ServiceOrderShowData ServiceOrderShowDataId">
              <b>
                {t('Orden de servicio')} #{serviceOrder?.id}
              </b>
              {serviceOrder?.status != undefined && (
                <div className="ServiceOrderShowRowBooleans">
                  <ServiceOrderStatusTag serviceOrderStatus={serviceOrder.status} />
                  {serviceOrder.created_automatically && (
                    <Tag color="geekblue">{t('Automática')}</Tag>
                  )}
                </div>
              )}
            </div>

            <div className="ServiceOrderShowData">
              <div>
                <b>{t('Fecha de creación')}: </b>
                <DateField value={serviceOrder?.created_at} />
              </div>
              <div>
                <b>{t('Fecha última modificación')}: </b>
                <DateField value={serviceOrder?.updated_at} />
              </div>

              <div>
                <b>{t('Creado por')}: </b>
                <TextField
                  value={
                    serviceOrder.created_by
                      ? `${(serviceOrder as any).created_by_str} (${serviceOrder.created_by})`
                      : ''
                  }
                />
              </div>
            </div>
          </div>
          {!hasTechnicianItem ||
          (userWorkArea != USER_WORK_AREA.LOGISTICS && userWorkArea != USER_WORK_AREA.SOFTWARE) ? (
            <ServiceOrderShowServiceInfo
              serviceOrder={serviceOrder}
              serviceOrderItems={serviceOrderItems}
              setServiceOrderItems={setServiceOrderItems}
              modifingAdminData={modifingAdminData}
              setModifingAdminData={setModifingAdminData}
              refetch={refetchAll}
            />
          ) : (
            <Tabs
              defaultActiveKey="1"
              items={[
                {
                  label: t('Servicio realizado'),
                  key: '1',
                  children: (
                    <ServiceOrderShowServiceInfo
                      serviceOrder={serviceOrder}
                      serviceOrderItems={serviceOrderItems}
                      setServiceOrderItems={setServiceOrderItems}
                      modifingAdminData={modifingAdminData}
                      setModifingAdminData={setModifingAdminData}
                      refetch={refetchAll}
                    />
                  ),
                },
                {
                  label: t('Logística'),
                  key: '2',
                  children: (
                    <ServiceOrderShowLogistic
                      serviceOrder={serviceOrder}
                      serviceOrderItems={serviceOrderItems}
                      setServiceOrderItems={setServiceOrderItems}
                      refetch={refetchAll}
                    />
                  ),
                  icon: serviceOrder.stock_technician_reviewed ? (
                    <CheckCircleFilled className="ServiceOrderShowLogisticIcon Success" />
                  ) : (
                    <WarningFilled className="ServiceOrderShowLogisticIcon Warning" />
                  ),
                },
              ]}
            />
          )}
        </div>
      )}
    </Show>
  );
};

export default ServiceOrderShow;
